import { useEffect, useState, useCallback } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useBlocker, useNavigate } from 'react-router-dom';

import { EyeIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import profilesAPI from '@/api/profiles';
import Button, { BUTTON_KIND } from '@/components/Button';
import { AdditionalContactInfo } from '@/components/EditProfilePage/AdditionalContactInfo';
import BulkEditWarningModal from '@/components/EditProfilePage/BulkEditWarningModal';
import { CompanyInformation } from '@/components/EditProfilePage/CompanyInfoForm';
import { ProfileManagementBulkEdit } from '@/components/EditProfilePage/ProfileManagement';
import { ProfileQuickFileLinks } from '@/components/EditProfilePage/QuickLinksForm';
import { ProfileSocialLinks } from '@/components/EditProfilePage/SocialLinksForm';
import {
  SharedProfilePageSchema,
  WorkingSharedProfilePageForm,
} from '@/components/EditProfilePage/schema';
import InfoPanelDivider from '@/components/InfoPanelDivider';
import InfoPanelFooter from '@/components/InfoPanelFooter';
import Layout from '@/components/Layout';
import LoadingAnimation from '@/components/LoadingAnimation';
import {
  ProfilePreview,
  ProfilePreviewModal,
} from '@/components/ProfilePreview';
import { UnsavedChangesModal } from '@/components/UnsavedChangesPrompt';
import COLORS from '@/constants/colors';
import MESSAGES from '@/constants/messages-en';
import useAppState from '@/hooks/useAppState';
import useAuth from '@/hooks/useAuth';
import usePreviewProfile, {
  PreviewProfileProvider,
} from '@/hooks/usePreviewProfile';
import IProfile, { IProfilePublic } from '@/types/IProfile';

/**
 * Type for form value getter function (watch or getValues from react-hook-form).
 */
type FormValueGetter = <T = any>(name: string) => T;

/**
 * Builds settings object for preview based on settings level.
 * When settings_level is 'organisation', uses organization settings from profile data.
 * When settings_level is 'profile', uses form values for profile-specific settings.
 * @param settingsLevel - 'organisation' or 'profile'
 * @param profileSettings - Organization-level settings from profile data (IOrganisationSettings from IProfilePublic)
 * @param getFormValue - Function to retrieve form values (watch or getValues)
 * @returns Settings object for ProfilePreview component
 */
function buildPreviewSettings(
  settingsLevel: 'organisation' | 'profile',
  profileSettings: any | undefined,
  getFormValue: FormValueGetter,
) {
  return settingsLevel === 'organisation' && profileSettings
    ? {
        bg_color: profileSettings.bg_color,
        text_color: profileSettings.text_color,
        button_bg_color: profileSettings.button_bg_color,
        button_text_color: profileSettings.button_text_color,
        logo: profileSettings.company_logo || null,
        logo_file_id: profileSettings.company_logo_file_id || null,
      }
    : {
        bg_color: getFormValue('settings.bg_color'),
        text_color: getFormValue('settings.text_color'),
        button_bg_color: getFormValue('settings.button_bg_color'),
        button_text_color: getFormValue('settings.button_text_color'),
        logo: getFormValue('settings.logo'),
        logo_file_id: getFormValue('settings.logo_file_id'),
      };
}

export default function EditSharedProfilePage() {
  const { orgID } = useAuth();
  const { multipleProfiles } = useAppState();
  const navigate = useNavigate();

  // Stable callbacks for queries to avoid recreations on every render
  const showProfilesShared = useCallback(async () => {
    if (orgID === undefined) {
      return;
    }

    const { data: profiles } = await profilesAPI.showProfilesShared(
      orgID,
      multipleProfiles,
    );

    return profiles.data;
  }, [orgID, multipleProfiles]);

  const showFirstProfile = useCallback(async () => {
    if (orgID === undefined) {
      return;
    }

    const { data } = await profilesAPI.showProfile(
      orgID,
      multipleProfiles[0],
    );

    return data.data;
  }, [orgID, multipleProfiles]);

  const { data: profile } = useQuery({
    queryKey: ['showProfilesShared', ...multipleProfiles],
    queryFn: showProfilesShared,
    enabled: orgID !== undefined,
    refetchOnWindowFocus: false,
  });

  // Query to get the first profile's hash (needed for preview)
  const { data: firstProfile } = useQuery({
    queryKey: ['showProfile', multipleProfiles[0]],
    queryFn: showFirstProfile,
    enabled: orgID !== undefined && multipleProfiles.length > 0,
    refetchOnWindowFocus: false,
  });
  // Stable callback for preview profile query (depends on firstProfile hash)
  const showPreviewProfile = useCallback(async (): Promise<IProfilePublic | undefined> => {
    if (!firstProfile?.profile_hash) {
      return;
    }

    const { data: previewProfile } = await profilesAPI.showProfileWithHashID(
      firstProfile.profile_hash,
      true, // bypass_analytics=true
    );

    return previewProfile.data;
  }, [firstProfile?.profile_hash]);


  // Query for preview profile data using hash with bypass_analytics
  const { data: previewProfile } = useQuery({
    queryKey: ['showProfileWithHashID', firstProfile?.profile_hash],
    queryFn: showPreviewProfile,
    enabled: firstProfile?.profile_hash !== undefined,
    refetchOnWindowFocus: false,
  });

  const methods = useForm<WorkingSharedProfilePageForm>({
    resolver: zodResolver(SharedProfilePageSchema),
  });

  useEffect(() => {
    if (multipleProfiles.length === 0) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (profile) {
      methods.reset({
        additional_information: profile.additional_information,
        contact_exchange_enabled: profile.contact_exchange_enabled,
        company_name: profile.company_name,
        company_phone_number: profile.company_phone_number,
        company_website: profile.company_website,
        city: profile.city,
        country: profile.country,
        state: profile.state,
        street_address: profile.street_address,
        profile_social_links: profile.profile_social_links,
        profile_file_links: profile.profile_file_links.map((link, index) => ({
          status: 'complete',
          order: index + 1,
          title: link.title || '',
          file: link.file,
          file_id: link.file_id || -999,
        })),
        profile_quick_links: profile.profile_quick_links.map((link, index) => ({
          order: index,
          title: link.title || '',
          value: link.value || '',
        })),
        extra_company_phone_numbers:
          profile.extra_company_phone_numbers ?? null,
        // Default to organisation settings and default profile design values
        // All profiles will have their `settings` and `settings_level` overwritten anyway
        settings: {
          logo: null,
          logo_file_id: null,
          bg_color: COLORS.organisation.defaultProfile.backgroundColor,
          text_color: COLORS.organisation.defaultProfile.textColor,
          button_bg_color:
            COLORS.organisation.defaultProfile.buttonBackgroundColor,
          button_text_color: COLORS.organisation.defaultProfile.buttonTextColor,
        },
        settings_level: 'organisation',
      });
    }
  }, [profile]);


  if (profile === undefined) {
    return (
      <Layout
        pageName="Edit shared information"
        description={`${multipleProfiles.length} profiles selected`}
        className="bg-gray-50"
      >
        <div className="flex items-center justify-center h-full">
          <LoadingAnimation className="w-16 h-16 mx-auto" />
        </div>
      </Layout>
    );
  }

  return (
    <PreviewProfileProvider previewProfile={previewProfile}>
      <FormProvider {...methods}>
        <Layout
          pageName="Edit shared information"
          description={`${multipleProfiles.length} profiles selected`}
          className="bg-gray-50"
        >
          <EditSharedProfileForm />
        </Layout>
      </FormProvider>
    </PreviewProfileProvider>
  );
}

function EditSharedProfileForm() {
  const { orgID } = useAuth();
  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isSubmitSuccessful },
  } = useFormContext<WorkingSharedProfilePageForm>();
  const navigate = useNavigate();
  const { multipleProfiles } = useAppState();

  const { mutateAsync: updateProfiles } = useMutation({
    mutationFn: updateProfilesShared,
    onSuccess: () => {
      toast.success(`${multipleProfiles.length} profiles updated successfully`);
    },
    onError: () => {
      toast.error(MESSAGES.error.generic);
    },
  });

  const blocker = useBlocker(isDirty && !isSubmitSuccessful);

  const [isBulkEditWarningModalOpen, setIsBulkEditWarningModalOpen] =
    useState(false);

  useEffect(() => {
    if (blocker.state === 'blocked' && isSubmitSuccessful) {
      blocker.proceed();
    }
  }, [blocker, isSubmitSuccessful]);

  async function onSubmit(data: WorkingSharedProfilePageForm) {
    // Build profile object for bulk_replace endpoint
    // Note: bulk_replace uses replacement semantics:
    // - Sending an array of links REPLACES all existing links
    // - Empty array [] DELETES all links
    // - Omitting the field KEEPS links unchanged
    const profile: Partial<IProfile> = {
      additional_information: data.additional_information,
      company_name: data.company_name,
      company_phone_number: data.company_phone_number,
      company_website: data.company_website,
      city: data.city,
      country: data.country,
      state: data.state,
      street_address: data.street_address,
      post_code: data.post_code,
      // Links are sent directly without from/to transformation
      // The bulk_replace endpoint will replace all links with these values
      profile_file_links: data.profile_file_links
        .filter(link => link.status !== 'incomplete')
        .map(({ status, ...link }) => ({
          title: link.title,
          file_id: link.file_id,
          file: link.file,
          order: link.order,
        })),
      profile_quick_links: data.profile_quick_links.map(link => ({
        title: link.title,
        value: link.value,
        order: link.order,
      })),
      profile_social_links: data.profile_social_links.map(link => ({
        type: link.type,
        value: link.value,
        order: link.order,
        personal: link.personal,
      })),
      contact_exchange_enabled: data.contact_exchange_enabled,
      settings: data.settings,
      settings_level: data.settings_level,
    };

    await updateProfiles(profile);

    navigate('/', {
      state: {
        success: MESSAGES.profile.edit.successMultiple,
      },
    });
  }

  async function updateProfilesShared(profile: Partial<IProfile>) {
    if (orgID === undefined) {
      return;
    }

    await profilesAPI.updateProfilesSharedNew({
      orgID,
      body: { ids: multipleProfiles, profile },
    });
  }

  const onInvalid = () => {
    toast.error('Please review and correct the form to continue');
  };

  return (
    <>
      <div className="flex flex-row pb-20 space-x-8">
        <div className="pt-6 max-w-full">
          <ProfileSocialLinks />
          <InfoPanelDivider />
          <CompanyInformation />
          <InfoPanelDivider />
          <ProfileQuickFileLinks />
          <InfoPanelDivider />
          <AdditionalContactInfo />
          <InfoPanelDivider />
          <ProfileManagementBulkEdit />
        </div>
        <EditProfilePreview />
      </div>
      <InfoPanelFooter>
        <div className="flex justify-between xl:justify-end space-x-4">
          <div className="xl:hidden">
            <EditProfilePreviewModal />
          </div>
          <div className="flex justify-end space-x-4">
            <Button
              buttonText="Cancel"
              kind={BUTTON_KIND.WHITE}
              onClick={() => navigate('/')}
            />
            <Button
              buttonText="Save changes"
              onClick={() => setIsBulkEditWarningModalOpen(true)}
              loading={isSubmitting}
            />
          </div>
        </div>
        {blocker.state === 'blocked' && (
          <UnsavedChangesModal
            proceed={() => setIsBulkEditWarningModalOpen(true)}
            cancel={blocker.proceed}
            reset={blocker.reset}
            isLoading={isSubmitting}
          />
        )}
        <BulkEditWarningModal
          isOpen={isBulkEditWarningModalOpen}
          setIsOpen={setIsBulkEditWarningModalOpen}
          onSuccess={handleSubmit(onSubmit, onInvalid)}
          isLoading={isSubmitting}
        />
      </InfoPanelFooter>
    </>
  );
}

function EditProfilePreview() {
  const { multipleProfiles } = useAppState();
  const { watch } = useFormContext<WorkingSharedProfilePageForm>();
  const { previewProfile } = usePreviewProfile();

  const settingsLevel = watch('settings_level');

  // Build settings based on settings_level
  // When 'organisation', uses organization settings from profile data
  // When 'profile', uses form values for profile-specific settings
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    watch,
  );

  const profileContent = {
    ...previewProfile,
    company_name: watch('company_name'),
    company_website: watch('company_website'),
    company_phone_number: watch('company_phone_number'),
    street_address: watch('street_address'),
    city: watch('city'),
    state: watch('state'),
    post_code: watch('post_code'),
    country: watch('country'),
    profile_social_links: watch('profile_social_links'),
    profile_quick_links: watch('profile_quick_links'),
    profile_file_links: (watch('profile_file_links') || [])
      .filter(
        link =>
          link.status !== 'incomplete' && typeof link.file_id === 'number',
      )
      .map(link => ({
        file_id: link.file_id as number,
        file: link.file,
        order: link.order,
        title: link.title,
      })),
  };

  return (
    <div
      className="hidden xl:block w-full max-w-[352px] md:max-w-[388px] h-fit sticky top-0 pt-8"
      style={{ zoom: 0.8 }}
    >
      <div className="p-4 rounded-md bg-white shadow-[0_0_0_1px_theme(colors.gray.300)]">
        <div className="py-1 px-3 text-sm rounded-full bg-brand-100 text-brand-500 w-fit place-self-center mb-4">
          Showing profile 1/{multipleProfiles.length}
        </div>
        {previewProfile === undefined ? (
          <ProfilePreviewSkeleton />
        ) : (
          <ProfilePreview content={profileContent} settings={settings} />
        )}
      </div>
    </div>
  );
}

function ProfilePreviewSkeleton() {
  return (
    <div className="transition-all transform shadow-xl rounded-4xl overflow-hidden w-full max-w-[320px] md:max-w-[356px] h-[625px] md:h-[695px]">
      <Skeleton width="100%" height="100%" />
    </div>
  );
}

function EditProfilePreviewModal() {
  const { watch } = useFormContext<WorkingSharedProfilePageForm>();
  const { previewProfile } = usePreviewProfile();

  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);

  const settingsLevel = watch('settings_level');

  // Build settings based on settings_level
  // When 'organisation', uses organization settings from profile data
  // When 'profile', uses form values for profile-specific settings
  const settings = buildPreviewSettings(
    settingsLevel,
    previewProfile?.settings,
    watch,
  );

  const profileContent = {
    ...previewProfile,
    company_name: watch('company_name'),
    company_website: watch('company_website'),
    company_phone_number: watch('company_phone_number'),
    street_address: watch('street_address'),
    city: watch('city'),
    state: watch('state'),
    post_code: watch('post_code'),
    country: watch('country'),
    profile_social_links: watch('profile_social_links'),
    profile_quick_links: watch('profile_quick_links'),
    profile_file_links: (watch('profile_file_links') || [])
      .filter(
        link =>
          link.status !== 'incomplete' && typeof link.file_id === 'number',
      )
      .map(link => ({
        file_id: link.file_id as number,
        file: link.file,
        order: link.order,
        title: link.title,
      })),
  };

  return (
    <>
      <Button
        buttonText="Preview"
        kind={BUTTON_KIND.WHITE}
        icon={<EyeIcon />}
        onClick={() => setIsProfilePreviewOpen(true)}
      />
      <ProfilePreviewModal
        isOpen={isProfilePreviewOpen}
        setIsOpen={setIsProfilePreviewOpen}
        settings={settings}
        content={profileContent}
      />
    </>
  );
}
